import React from "react"
import { Container, Card, CardBody, Alert } from "reactstrap"
import { useQuery } from "react-query"
import { Link, useSearchParams } from "react-router-dom"
import { withTranslation } from "react-i18next"
import { GENERAL } from "../../api/general"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import PaginatedDataTable from "components/PaginatedDataTable"

const noop = () => {}

// State -> LGA -> Ward drill-down. The selection lives in the URL
// (?stateId&stateName&lgaId&lgaName) so refresh and the back button work; the
// backend picks the level from which ids are sent.
const RegistrationCounts = props => {
  const [searchParams] = useSearchParams()
  const stateId = searchParams.get("stateId")
  const stateName = searchParams.get("stateName") || stateId
  const lgaId = searchParams.get("lgaId")
  const lgaName = searchParams.get("lgaName") || lgaId
  const level = lgaId ? "ward" : stateId ? "lga" : "state"

  const { data, isLoading } = useQuery({
    queryKey: ["registrationCounts", stateId, lgaId],
    queryFn: () => GENERAL.generateInvoice({ stateId, lgaId }),
  })

  // services/get() returns the error response instead of throwing, so a
  // missing list means the request failed (e.g. 500 when VitalReg is down).
  const rows = data?.palliative
  const failed = !isLoading && !Array.isArray(rows)

  const drillDown = row => {
    if (level === "state") {
      return new URLSearchParams({ stateId: row.id, stateName: row.name })
    }
    if (level === "lga") {
      return new URLSearchParams({
        stateId,
        stateName,
        lgaId: row.id,
        lgaName: row.name,
      })
    }
    return null // wards are the last level; "Unassigned" has a null id
  }

  const columns = [
    {
      name: "#",
      selector: (row, index) => index + 1,
      width: "4rem",
    },
    {
      name: { state: "State", lga: "LGA", ward: "Ward" }[level],
      selector: row => row.name,
      sortable: true,
    },
    {
      name: "Registrars",
      selector: row => row.registrarCount,
      sortable: true,
    },
    {
      name: "Total Registrations",
      selector: row => row.totalRegistrations,
      sortable: true,
    },
    {
      name: "",
      cell: row => {
        const next = drillDown(row)
        return next ? (
          <Link to={`/registration-counts?${next}`}>
            {level === "state" ? "View LGAs" : "View Wards"}
          </Link>
        ) : null
      },
    },
  ]

  //meta title
  document.title = "Ward Registrars Management System"

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Registration Counts")}
            breadcrumbItem={props.t("Registration Counts")}
            hasBackBtn={level !== "state"}
          />

          <Card>
            <CardBody>
              <div className="h4 card-title">
                <Link to="/registration-counts">All States</Link>
                {stateId && (
                  <>
                    {" / "}
                    {lgaId ? (
                      <Link
                        to={`/registration-counts?${new URLSearchParams({
                          stateId,
                          stateName,
                        })}`}
                      >
                        {stateName}
                      </Link>
                    ) : (
                      stateName
                    )}
                  </>
                )}
                {lgaId && ` / ${lgaName}`}
              </div>

              {isLoading && <p className="text-muted">Loading...</p>}
              {failed && (
                <Alert color="danger">
                  {data?.data?.message ||
                    "Unable to load registration counts. Please try again."}
                </Alert>
              )}
              {!isLoading && !failed && (
                // ponytail: lists are small (<=37 states, ~44 LGAs, a few dozen
                // wards) so all rows render on one page; add client-side
                // paging/search if a level ever grows past ~100 rows.
                <PaginatedDataTable
                  columns={columns}
                  data={rows}
                  totalRecords={rows.length}
                  handlePageChange={noop}
                  handlePerRowsChange={noop}
                />
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withTranslation()(RegistrationCounts)
